/**
 * web/screens/spaceGame.ts
 * DOM host for the World 2 space-shmup scene.
 *
 * Mirrors `app/space-game.tsx` — orientation/UI state + SpaceScene
 * lifecycle, with the platformer-specific pieces (cutscene, level-complete
 * star rating, persistWorld1Completion) stripped in favour of the
 * SpaceHUD + sector-cleared overlay. Engine work is delegated to
 * {@link SpaceScene} and the shared {@link runGameRenderFrame} loop
 * (which internally swaps to the space draw chain via
 * `spaceState.isRunning`).
 *
 * @module web/screens/spaceGame
 */

/// <reference lib="dom" />

import type { Route } from '../router';
import { injectStyles } from '../styles';
import { loadGameAssets } from '../assets';
import { mountSpaceHud } from '../spaceHud';
import { mountTouchControls } from '../touchControls';
import { attachKeyboardListeners, clearAllHeld } from '../keyboard';
import {
  showPauseOverlay,
  showLevelCompleteOverlay,
  showGameOverOverlay,
  type OverlayHandle,
} from '../overlays';

import { runGameRenderFrame } from '@src/game/rendering/gameRenderFrame';
import { getRenderAssets } from '@src/game/rendering/renderAssetStore';
import { SpaceScene } from '@src/game/scenes/SpaceScene';
import {
  SPACE_LEVEL_2_1,
  getSpaceLevel,
  type SpaceLevelConfig,
} from '@src/game/levels/spaceLevels';
import { persistSpaceLevelCompletion } from '@src/game/levels/spaceLevelSession';
import { getCollectedShardIdsThisSession } from '@src/game/engine/spaceShardSystem';
import { getCollectedSeedIdsThisSession } from '@src/game/engine/spaceLootSystem';
import { persistDoubleJumpUnlock } from '@src/game/services/abilityUnlocks';
import { spaceState } from '@src/game/engine/spaceState';
import { clearAllInputState } from '@src/game/input/inputState';
import { getUserSetting } from '@src/game/services/userSettings';
import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
} from '@src/constants/gameConstants';

const POLL_MS = 200;

/** Resolve `?level=` → {@link SpaceLevelConfig}, falling back to 2-1. */
function resolveSpaceConfig(raw: string | null): SpaceLevelConfig {
  if (raw === null) return SPACE_LEVEL_2_1;
  const lookup = getSpaceLevel(raw);
  return lookup ?? SPACE_LEVEL_2_1;
}

/** Build the in-game pause button (top-right). */
function buildPauseButton(onPause: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sf-game-pause-btn';
  btn.setAttribute('aria-label', 'Pause game');
  btn.textContent = '⏸';
  btn.addEventListener('click', onPause);
  return btn;
}

/** Space-game route factory. */
export const spaceGameScreen: Route = (router, params) => {
  injectStyles();
  const levelConfig = resolveSpaceConfig(params.get('level'));

  const root = document.createElement('section');
  root.className = 'sf-game-root';
  root.dataset['screen'] = 'space-game';

  const canvas = document.createElement('canvas');
  canvas.className = 'sf-game-canvas';
  canvas.width = VIEWPORT_WIDTH;
  canvas.height = VIEWPORT_HEIGHT;
  root.append(canvas);
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.imageSmoothingEnabled = false;

  const scene = new SpaceScene();
  scene.init(levelConfig);

  // ── DOM overlays + mounts ────────────────────────────────────────────────
  const hud = mountSpaceHud(root);
  const showTouch = getUserSetting('showTouchControls');
  const touch = showTouch ? mountTouchControls(root, { showUp: true }) : null;
  const detachKeyboard = attachKeyboardListeners();

  let paused = false;
  let levelComplete = false;
  let gameOver = false;
  let overlay: OverlayHandle | null = null;

  const closeOverlay = (): void => {
    if (overlay) { overlay.destroy(); overlay = null; }
  };

  const setActiveGate = (): boolean => !paused && !levelComplete && !gameOver;

  const handleResume = (): void => {
    clearAllHeld();
    paused = false;
    closeOverlay();
    hud.setActive(true);
  };

  const handleRestart = (): void => {
    clearAllHeld();
    scene.destroy();
    scene.init(levelConfig);
    paused = false;
    levelComplete = false;
    gameOver = false;
    closeOverlay();
    hud.setActive(true);
  };

  const handleQuit = (): void => {
    router.back('/world-map');
  };

  const handlePause = (): void => {
    if (!setActiveGate()) return;
    clearAllHeld();
    paused = true;
    hud.setActive(false);
    closeOverlay();
    overlay = showPauseOverlay(root, {
      onResume: handleResume,
      onRestart: handleRestart,
      onQuit: handleQuit,
    });
  };

  root.append(buildPauseButton(handlePause));

  // ── Asset load (idempotent) ──────────────────────────────────────────────
  void loadGameAssets();

  // ── RAF loop ─────────────────────────────────────────────────────────────
  let lastTime = performance.now();
  let rafId = 0;
  const tick = (): void => {
    const now = performance.now();
    const delta = now - lastTime;
    lastTime = now;
    if (ctx) {
      runGameRenderFrame(ctx, getRenderAssets(), delta, setActiveGate(), now);
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  // ── Completion polling ───────────────────────────────────────────────────
  const pollId = globalThis.setInterval(() => {
    if (!setActiveGate()) return;
    if (spaceState.gameOver && !gameOver) {
      gameOver = true;
      hud.setActive(false);
      closeOverlay();
      overlay = showGameOverOverlay(root, {
        onRestart: handleRestart,
        onQuit: handleQuit,
      });
      return;
    }
    if (spaceState.levelComplete && !levelComplete) {
      levelComplete = true;
      hud.setActive(false);
      const stats = {
        seeds: 0,
        time: spaceState.elapsedSeconds,
        score: 0,
      };
      // DECISION: Boss 2-B path persists the double-jump unlock BEFORE the
      // level-completion flag so both writes share a single load→mutate→save
      // round-trip and the ability flag isn't stomped by the completion
      // writer (mirrors app/space-game.tsx).
      const levelId = levelConfig.id;
      // DECISION: Snapshot the collected shard + seed ids here
      // (synchronous reads off the engine modules) so a fast Restart
      // can't drain the session lists between this tick and the
      // async save() call below.
      const collectedShardIds = getCollectedShardIdsThisSession();
      const collectedSeedIds = getCollectedSeedIdsThisSession();
      void (async (): Promise<void> => {
        if (levelId === '2-B') await persistDoubleJumpUnlock();
        await persistSpaceLevelCompletion(
          levelId,
          collectedShardIds,
          collectedSeedIds,
        );
      })();
      closeOverlay();
      overlay = showLevelCompleteOverlay(root, {
        seeds: stats.seeds,
        time: stats.time,
        score: stats.score,
        onRestart: handleRestart,
        onQuit: handleQuit,
      });
    }
  }, POLL_MS);

  // ── Visibility / blur — clear stuck input ────────────────────────────────
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') clearAllInputState();
  };
  document.addEventListener('visibilitychange', onVisibility);

  // ── Pause via Escape ─────────────────────────────────────────────────────
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' && setActiveGate()) handlePause();
  };
  document.addEventListener('keydown', onKey);

  document.getElementById('app')?.append(root);

  return {
    destroy: (): void => {
      cancelAnimationFrame(rafId);
      globalThis.clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('keydown', onKey);
      detachKeyboard();
      touch?.destroy();
      hud.destroy();
      closeOverlay();
      scene.destroy();
      root.remove();
    },
  };
};
